import React, { useState } from "react";
import Datetime from "react-datetime";
import { CalendarBlank } from "phosphor-react";
import "react-datetime/css/react-datetime.css";
import "./index.scss";
import moment from "moment";

function DateInput({
  className,
  handleChange,
  name,
  value,
  placeholder,
  label,
  isError,
  error,
  isValidDate,
  isRequired = false,
  disabled = false,
  isDateOfBirth = false,
  ...rest
}) {
  const [isActive, setActive] = useState(false);
  const [inputError, setInputError] = useState(null);
  const [localValue, setLocalValue] = useState(
    value && moment(value).isValid() ? moment(value).format("MM/DD/YYYY") : ""
  );

  const formatForBackend = (dateString) => {
    return moment(dateString, "MM/DD/YYYY", true).format("YYYY-MM-DD");
  };

  const validateDate = (dateString, customIsValidDate, isDOB) => {
    const parsedDate = moment(dateString, "MM/DD/YYYY", true);

    if (!parsedDate.isValid()) {
      return { isValid: false, error: "Invalid date. Please use 'MM/DD/YYYY'" };
    }
    if (customIsValidDate && !customIsValidDate(parsedDate)) {
      return {
        isValid: false,
        error: "Invalid date",
      };
    }
    if (isDOB) {
      const today = moment();
      if (parsedDate.isAfter(today)) {
        return {
          isValid: false,
          error: "Invalid date",
        };
      }
    }
    const minDate = moment("01/01/1800", "MM/DD/YYYY");
    if (parsedDate.isBefore(minDate)) {
      return { isValid: false, error: "Dates before 1800 are not allowed" };
    }

    return { isValid: true, error: null };
  };

  const handleInputChange = (e) => {
    let input = e.target.value.replace(/[^0-9]/g, "");
    let formatted = "";
    let month = "";
    let day = "";
    let year = "";

    if (input.length > 0) {
      month = input.substring(0, 2);
      if (parseInt(month) > 12) {
        month = "12";
      }
      formatted = month;
    }
    if (input.length >= 3) {
      day = input.substring(2, 4);
      const tempDate = moment(`${month}/01/2025`, "MM/DD/YYYY", true);
      const daysInMonth = tempDate.isValid() ? tempDate.daysInMonth() : 31;
      if (parseInt(day) > daysInMonth) {
        day = daysInMonth.toString().padStart(2, "0");
      }
      formatted = `${month}/${day}`;
    }
    if (input.length >= 5) {
      year = input.substring(4, 8);
      formatted = `${month}/${day}/${year}`;
    }

    if (input.length >= 8) {
      const validation = validateDate(formatted, isValidDate, isDateOfBirth);
      setInputError(validation.error);
      if (validation.isValid) {
        setLocalValue(formatted);
        const backendDate = formatForBackend(formatted);
        handleChange(backendDate);
      } else {
        setLocalValue(formatted);
        handleChange(null);
      }
    } else {
      setLocalValue(formatted);
      setInputError(null);
      handleChange(null);
    }
  };

  const handleDateChange = (date) => {
    let dateString = "";
    if (typeof date === "string") {
      dateString = date;
    } else {
      dateString = date.format("MM/DD/YYYY");
    }
    const validation = validateDate(dateString, isValidDate, isDateOfBirth);
    setInputError(validation.error);
    setLocalValue(dateString);
    if (validation.isValid) {
      const backendDate = formatForBackend(dateString);
      handleChange(backendDate);
    } else {
      handleChange(null);
    }
  };

  const defaultIsValidDate = (current) => {
    const minDate = moment("01/01/1900", "MM/DD/YYYY");
    if (isDateOfBirth) {
      const today = moment();
      return current.isSameOrBefore(today) && current.isSameOrAfter(minDate);
    }
    return current.isSameOrAfter(minDate);
  };

  const renderInput = (
    { name, value, placeholder, isError, isActive },
    openCalendar
  ) => {
    return (
      <div className="position-relative w-100">
        <input
          type="text"
          className={`theme-input input w-100 z-1 ${
            isError || inputError ? "border-danger" : ""
          } ${isActive ? "border-active" : ""}`}
          name={name}
          value={localValue}
          placeholder={placeholder}
          onChange={handleInputChange}
          disabled={disabled}
          maxLength={10}
          {...rest}
        />
        <span
          className="position-absolute top-50 end-0 translate-middle p-1 pe-0 z-3 cursor-pointer"
          onClick={openCalendar}
        >
          <CalendarBlank size={30} fontWeight={800} color="#60606090" />
        </span>
      </div>
    );
  };

  return (
    <div
      className={` ${className ? className : ""} input-otr ${disabled &&
        "disabled-input"}`}
    >
      <label htmlFor={name} className="input-label">
        {label} {isRequired && <span style={{ color: "red" }}>*</span>}
      </label>
      <Datetime
        initialViewMode="days"
        renderInput={renderInput}
        dateFormat="MM/DD/YYYY"
        timeFormat={false}
        closeOnSelect
        placeholder={placeholder}
        label="Date"
        inputProps={{
          isError: isError || !!inputError,
          name,
          placeholder,
          isActive,
          disabled,
        }}
        onChange={handleDateChange}
        onOpen={() => setActive(true)}
        onClose={() => setActive(false)}
        closeOnClickOutside={true}
        isValidDate={isValidDate || defaultIsValidDate}
      />
      {inputError || error ? (
        <div className="text-danger mt-1">{inputError || error}</div>
      ) : null}
    </div>
  );
}

export default DateInput;
